import { computed, Directive, input } from '@angular/core';

import { buttonEnumInputTransformer, ButtonEnumInputType } from './button-common-transformers';

export enum CommonButtonOutlinedSize {
	XS = 'XS',
	S = 'S',
	M = 'M',
	L = 'L',
}

export enum CommonButtonOutlinedState {
	DEFAULT = 'DEFAULT',
	SELECTED = 'SELECTED',
	DISABLED = 'DISABLED',
}

const CLASSES_BY_SIZE_MAP: Record<CommonButtonOutlinedSize, string[]> = {
	[CommonButtonOutlinedSize.XS]: ['rounded-0.75', 'py-0.5', 'px-2'],
	[CommonButtonOutlinedSize.S]: ['rounded-1', 'py-0.75', 'px-2'],
	[CommonButtonOutlinedSize.M]: ['rounded-1', 'py-1', 'px-2'],
	[CommonButtonOutlinedSize.L]: ['rounded-1', 'py-1', 'px-3'],
};

const CLASSES_BY_STATE_MAP: Record<CommonButtonOutlinedState, string[]> = {
	[CommonButtonOutlinedState.DEFAULT]: ['border border-line-230', 'fc-800', 'hover:bg-standard-hover'],
	[CommonButtonOutlinedState.SELECTED]: ['fc-800', 'bg-primary/20', 'border-primary-20'],
	[CommonButtonOutlinedState.DISABLED]: ['border border-line-250', 'fc-200'],
};

@Directive({
	selector: '[commonButtonOutlined],[commonButtonBordered]',
	standalone: true,
	host: {
		class: 'flex items-center justify-start gap-0.75 border',
		'[class]': '$classes()',
		'[attr.data-size]': '$size()',
		'[attr.data-state]': '$state()',
		'[attr.disabled]': '$disabled()',
	},
})
export class CommonButtonOutlinedDirective {
	public readonly $size = input<
		CommonButtonOutlinedSize,
		ButtonEnumInputType<CommonButtonOutlinedSize>
	>(CommonButtonOutlinedSize.M, {
		alias: 'size',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonOutlinedSize),
				CommonButtonOutlinedSize.M
			),
	});

	public readonly $state = input<
		CommonButtonOutlinedState,
		ButtonEnumInputType<CommonButtonOutlinedState>
	>(CommonButtonOutlinedState.DEFAULT, {
		alias: 'state',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonOutlinedState),
				CommonButtonOutlinedState.DEFAULT
			),
	});

	protected readonly $classes = computed(() => {
		const size = this.$size();
		const state = this.$state();

		const classesBySize = CLASSES_BY_SIZE_MAP[size] ?? [];
		const classesByState = CLASSES_BY_STATE_MAP[state] ?? [];

		const classes = [...classesBySize, ...classesByState];

		return classes.join(' ');
	});

	protected readonly $disabled = computed(() => {
		const state = this.$state();
		return state === CommonButtonOutlinedState.DISABLED;
	});
}
